import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { useApiUrl, useDataProvider } from "@refinedev/core";
import _ from "lodash";

import { IIdentity } from "@/providers/auth-provider";

export interface MembersContextType {
  // State
  members: IIdentity[];
  refetch: () => void;
}

const MembersContext = createContext<MembersContextType | undefined>(undefined);

interface MembersProviderProps {
  children: ReactNode;
}

export function MembersContextProvider({ children }: MembersProviderProps) {
  const [members, setMembers] = useState<IIdentity[]>([]);

  const { custom } = useDataProvider()();
  const apiUrl = useApiUrl("default");

  const fetch = async () => {
    const result = await custom<IIdentity[]>?.({
      url: `${apiUrl}/users/admins`,
      method: "get",
      query: {
        queryKey: ["members"],
        staleTime: 1000 * 60 * 60 * 24,
        refetchOnMount: false,
      },
    });

    if (!result || _.isEmpty(result?.data)) return;

    setMembers([
      ...result.data,
      {
        id: "integration-api-key",
        displayName: "Tina",
        email: "tina@mood.com",
        photoUrl: "/tina-icon.png",
        adminMetadata: { role: "humanoid" },
      } as IIdentity,
    ]);
  };

  useEffect(() => {
    fetch();
  }, []);

  const value: MembersContextType = {
    members,
    refetch: fetch,
  };

  return (
    <MembersContext.Provider value={value}>{children}</MembersContext.Provider>
  );
}

export function useMembers() {
  const context = useContext(MembersContext);
  if (context === undefined) {
    throw new Error("useMembers must be used within a MembersContextProvider");
  }
  return context;
}
